import {Injectable} from "@angular/core";
import {centerPopup, defaultPopupOptions, stringifyPopupOptions} from "../misc/dom-helper";
import {SmartsheetTokenModel} from "./smartsheet-token.model";
import {SmartsheetWorkspaceModel} from "./smartsheet-workspace.model";
import {HttpClient} from "@angular/common/http";
import {map} from "rxjs/operators";
import {exposePropertyInDebugMode} from "../misc/expose-item-in-debug-mode.function";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {
  SmartsheetWorkspaceInfo,
  SmartsheetWorkspaceListDialogComponent,
} from "./smartsheet-workspace-list.dialog.component";

@Injectable()
export class SmartsheetService {
  private uri = "/api/smartsheet";

  constructor(private http: HttpClient, private dialogService: DialogService) {
    exposePropertyInDebugMode("SmartsheetService", {
      enumerable: true,
      configurable: true,
      get: () => this,
    });
  }

  async logIn(): Promise<SmartsheetTokenModel> {
    const url = "/api/smartsheet/auth";
    const initialPopupBounds = centerPopup(800, 600);
    const popupOptions = stringifyPopupOptions({...defaultPopupOptions, ...initialPopupBounds});

    const win = window.open(url, "", popupOptions);
    if (!win) throw new Error("Login failed.");

    return new Promise<SmartsheetTokenModel>((resolve, reject) => {
      window["SMARTSHEET_DIALOG_DONE"] = (value) => {
        resolve(new SmartsheetTokenModel(value));
      };

      setTimeout(
        () => {
          if (win) {
            try {
              win.close();
            } catch {
              console.error("Could not close login window.");
            }
          }

          reject(new Error("Login timed out."));
        },
        30 * 60 * 1000,
      );
    });
  }

  async getWorkspaceList(token: SmartsheetTokenModel, projectId: string): Promise<SmartsheetWorkspaceModel[]> {
    const body = {accessToken: token.access_token, projectId};

    return this.http.post<{workspaces: SmartsheetWorkspaceModel[]}>(`${this.uri}/workspaces`, body)
      .pipe(map(it => SmartsheetWorkspaceModel.fromList(it.workspaces)))
      .toPromise();
  }

  async saveSmartsheetWorkspace(
    token: SmartsheetTokenModel,
    workspaces: SmartsheetWorkspaceModel[],
    projectId: string,
  ) {
    const body = {
      token: JSON.stringify(token),
      smartsheetWorkspaces: workspaces.map(ws => ({id: ws.id, name: ws.name})),
      projectId,
    };

    return this.http.post(`${this.uri}/save`, body).toPromise();
  }

  /**
   * Get a Smartsheet Workspace ID and link it to a project.
   * @param projectId
   * @param selectedWorkspaces
   * @returns {SmartsheetWorkspaceModel|undefined} - undefined means no change, result.id === null means removed
   */
  async promptUserForSmartsheetWorkspaceId(projectId: string, selectedWorkspaces: any[]):
    Promise<SmartsheetWorkspaceInfo> {
    return this.dialogService.showDialog(SmartsheetWorkspaceListDialogComponent, {projectId, selectedWorkspaces});
  }

  async syncProject(projectId: string, type: string): Promise<any> {
    return this.http.get(`${this.uri}/project-sync?id=${projectId}&type=${type}`).toPromise();
  }
}
